<template>
  <KekkleCard shadow="blue">
    <div v-if="initializing" class="flex items-center justify-center py-8">
      <div
        class="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"
      />
    </div>
    <div v-else>
      <h1 class="text-xl font-bold dark:text-slate-200">
        {{ $t("pages.complete.title") }}
      </h1>
      <p class="mb-4 dark:text-slate-200">
        {{ $t("pages.complete.subtitle") }}
      </p>

      <div class="mb-4 flex justify-center">
        <ProfilePictureInput
          :current-image-url="profilePicturePreviewUrl"
          :label-disabled="true"
          @change="handleFileChange"
        />
      </div>

      <div class="mb-4 w-full">
        <TextInput
          v-model="name"
          :placeholder="$t('global.inputs.placeholders.your_name')"
          :label="$t('global.inputs.labels.your_name')"
          :has-error="validation.errors.name"
          :error-message="$t('global.inputs.errors.name')"
        />
        <p class="mt-1 text-sm text-gray-500 dark:text-slate-400">
          {{ $t("pages.complete.name_hint") }}
        </p>
      </div>

      <div class="mb-4">
        <DateInput
          v-model="birthday"
          :label="$t('global.inputs.labels.birthday')"
        />
        <p class="mt-1 text-sm text-gray-500 dark:text-slate-400">
          {{ $t("pages.complete.we_will_add_you_birthday_to_calendar") }}
        </p>
      </div>

      <InformationText
        v-if="error"
        color="red"
        class="mb-4"
        :information="error"
      >
      </InformationText>

      <DefaultButton
        :loading="saving"
        :disabled="!canSubmit"
        :full-width="true"
        @click="completeProfile"
      >
        <p>{{ $t("global.inputs.labels.save") }}</p>
      </DefaultButton>
    </div>
  </KekkleCard>
  <p
    class="my-8 cursor-pointer text-center font-medium text-black-dark underline dark:text-slate-200"
    @click="logout()"
  >
    {{ $t("global.inputs.labels.logout") }}
  </p>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { logEvent } from "firebase/analytics";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import * as Sentry from "@sentry/vue";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { uploadUserProfilePicture } from "@/helpers/firestorage/UserPictureService";
import { createUserDoc } from "@/helpers/firestore/UserService";
import {
  resolveInitialName,
  resolveInitialProfilePicture,
} from "@/helpers/utils/profile-defaults";
import { validateUserProfileData } from "@/helpers/utils/validate-user";
import { parseDateString } from "@/helpers/utils/validate-user";
import {
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import TextInput from "@/components/Inputs/TextInput.vue";
import DateInput from "@/components/Inputs/DateInput.vue";
import KekkleCard from "@/components/ui/KekkleCard.vue";
import ProfilePictureInput from "@/components/ui/ProfilePictureInput.vue";
import InformationText from "@/components/InformationText.vue";

const router = useRouter();
const { t } = useI18n();
const authStore = useAuthStore();
const userStore = useUserStore();
const groupsStore = useGroupsStore();

const name = ref("");
const birthday = ref("2000-01-01");
const profilePictureFile = ref<File | null>(null);
const profilePicturePreviewUrl = ref("");
const saving = ref(false);
const initializing = ref(true);
const error = ref("");

const validation = computed(() =>
  validateUserProfileData(name.value, birthday.value),
);

const canSubmit = computed(
  () =>
    validation.value.isValid &&
    profilePictureFile.value !== null &&
    !saving.value,
);

onMounted(async () => {
  const authUser = authStore.firebaseAuthUser;
  name.value = resolveInitialName(authUser?.displayName, authUser?.email);
  profilePictureFile.value = await resolveInitialProfilePicture(
    authUser?.photoURL,
    name.value,
  );
  profilePicturePreviewUrl.value = URL.createObjectURL(
    profilePictureFile.value,
  );
  initializing.value = false;
});

function handleFileChange(file: File) {
  profilePictureFile.value = file;
  profilePicturePreviewUrl.value = URL.createObjectURL(file);
}

async function completeProfile() {
  if (saving.value || !canSubmit.value || !profilePictureFile.value) {
    setWarningToast({ message: t("global.toast.check_all_fields") });
    return;
  }

  const uuid = authStore.authUuid;
  if (!uuid) return;

  saving.value = true;
  error.value = "";
  try {
    await uploadUserProfilePicture(uuid, profilePictureFile.value, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1920,
    });

    await createUserDoc(uuid, {
      name: name.value.trim(),
      profile_picture: `users/${uuid}/profile_pictures/${uuid}`,
      birthday: parseDateString(birthday.value),
    });

    setSuccessToast({ message: t("global.toast.saved_successfully") });
    await userStore.refreshUserData();
    await groupsStore.refreshAllUserGroupsData();
    await router.push({ name: "Group" });

    logEvent(firebaseAnalytics, "completed_profile", {
      name: "completed_profile",
    });
  } catch (error_) {
    Sentry.captureException(error_);
    error.value = t("pages.complete.save_failed");
  } finally {
    saving.value = false;
  }
}

function logout() {
  authStore.logout();
}
</script>
